<template>
    <label class="ds-radio" :class="[wrapperClass, rootClasses]" :data-test="dataTestLabel" :data-testid="dataTestid">
        <input type="radio" class="ds-radio__input" :id :name :value :disabled v-model="modelValue" v-bind="attrs" />
        <div
            class="ds-radio__radio"
            tabindex="0"
            @keydown="radioKeydownEventHandler"
            role="radio"
            :aria-checked="isChecked"
            :aria-disabled="disabled"
            :aria-labelledby="ariaLabelId"
        ></div>
        <div class="ds-radio__texts" v-if="label">
            <span class="ds-radio__label" :data-test="dataTestText" :id="ariaLabelId">{{ label }}</span>
            <span v-if="supportingText" class="ds-radio__message" :data-test="dataTestDescription">{{
                supportingText
            }}</span>
        </div>
        <slot></slot>
    </label>
</template>

<script lang="ts" setup generic="T extends DsFormFieldValueType">
import { computed, useAttrs } from "vue";
import type { Theme } from "../../types/theme";
import { DsFormFieldValueType } from "../models/ds-form-field-models";
import { DsRadioOptionModel } from "./ds-radio/ds-radio-models";

const props = defineProps<
    DsRadioOptionModel & {
        /**
         * Unique identifier for the radio button. This identifier is nessary for accessibility.
         */
        id: string;

        /**
         * Group name for the radio button.
         */
        name: string;

        /**
         * Generic override for the input value.
         */
        value: T;

        /**
         * Theme for the component. Defaults to "light" theme.
         */
        theme?: Theme;

        dataTestid?: string;
    }
>();

defineOptions({ inheritAttrs: false });
const { class: wrapperClass, ...attrs } = useAttrs();

const modelValue = defineModel<T>();

const radioKeydownEventHandler = (event: KeyboardEvent) => {
    if (!props.disabled && (event.key === "Enter" || event.key === "Space")) {
        modelValue.value = props.value;
    }
};

const ariaLabelId = computed(() => `label-${props.id.replace(/\s+/g, "-").toLowerCase()}`);

const isChecked = computed(() => modelValue.value == props.value);

const rootClasses = computed(() => {
    return [
        props.theme === "dark" ? "theme--dark" : "",
        props.disabled ? "disabled" : "",
        isChecked.value ? "checked" : "",
    ];
});
</script>
